/**
 * @file Permissions.jsx
 * @description Permissions component for displaying and managing permissions.
 * @component Permissions
 * @exports Permissions - Main component for displaying and managing permissions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { TextField } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getUserRoles } from "../../../services/adminModule/permissions/permissionsServices.js";
import { ROLE_ERRORS } from "../../../common/constants/adminModule/roleConstants.js";
import { SIZES, VARIANTS, AUTO_COMPLETE, SORT_ORDERS } from "../../../common/constants/commonConstants.js";
import { PERMISSION_STRINGS, PERMISSION_TABLE_COLUMNS, PERMISSION_KEYS } from "../../../common/constants/adminModule/permissionConstants.js";
import PermissionsTable from "./PermissionsTable.jsx";

const Permissions = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [userRoles, setUserRoles] = useState([]);
    const [searchRole, setSearchRole] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(PERMISSION_KEYS.ROLE_NAME);

    useEffect(() => { fetchRoles(); }, []);

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            setUserRoles(await getUserRoles());
        }
        catch (error) {
            console.error(ROLE_ERRORS.FETCH_ROLES_ERROR, error);
        }
        finally {
            setLoadingRoles(false);
        }
    };

    const { rolesToDisplay, filteredRolesLength } = useMemo(() => {
        const filteredRoles = userRoles.filter((userRole) =>
            userRole.rolename.toLowerCase().includes(searchRole.toLowerCase())
        );
        return {
            rolesToDisplay: filteredRoles
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredRolesLength: filteredRoles.length,
        };
    }, [userRoles, searchRole, order, orderBy, page, rowsPerPage]);

    const handlePermissionClick = ({ roleId, rolename }) => {
        const currentPath = location.pathname;
        const nextPath = `${currentPath}/manage-permissions/${roleId}`;
        navigate(nextPath, { state: { rolename } });
    };

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            <Grid container sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size={SIZES.SMALL}
                    value={searchRole}
                    variant={VARIANTS.OUTLINED}
                    autoComplete={AUTO_COMPLETE.OFF}
                    label={PERMISSION_STRINGS.SEARCH_ROLES}
                    onChange={e => setSearchRole(e.target.value)}
                />
            </Grid>
            <PermissionsTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                roles={rolesToDisplay}
                isLoading={loadingRoles}
                rowsPerPage={rowsPerPage}
                onEdit={handlePermissionClick}
                setRowsPerPage={setRowsPerPage}
                columns={PERMISSION_TABLE_COLUMNS}
                handleRequestSort={handleRequestSort}
                filteredRolesLength={filteredRolesLength}
            />
        </>
    );
};

export default Permissions;